/**
 * Analytics Hub Help Content
 *
 * Comprehensive help for the Analytics Hub and all 14 analytics sub-pages.
 * Includes team-level and admin/org-level analytics.
 */

import type { HelpContent } from '../types';
import {
  createHelpContent,
  createSection,
  createFAQ,
  createTip,
  createWalkthrough,
  createTroubleshooting,
  createRelatedLink,
} from '../utils';

// =============================================================================
// MAIN ANALYTICS HUB HELP
// =============================================================================

export const analyticsHelp: HelpContent = createHelpContent({
  id: 'analytics',
  title: 'Analytics Hub',
  description: 'Insights, reports, and data visualizations across your organization',
  lastUpdated: '2026-02-08',
  version: '2.0',

  // ─────────────────────────────────────────────────────────────────────────
  // TIPS
  // ─────────────────────────────────────────────────────────────────────────
  tips: [
    createTip(
      'tip-date-range',
      'pro-tip',
      'Date Range Filters',
      'Most reports support date range selection. Adjust the time period to focus on specific weeks, months, or quarters for more targeted insights.'
    ),
    createTip(
      'tip-export',
      'info',
      'Export Options',
      'Look for the export button in report toolbars. Most analytics can be exported to CSV for further analysis in spreadsheets.'
    ),
    createTip(
      'tip-role-access',
      'warning',
      'Access Levels',
      'Some analytics are only visible to team leads, managers, or admins. If you cannot see a report, check with your manager about access permissions.'
    ),
  ],

  // ─────────────────────────────────────────────────────────────────────────
  // SECTIONS
  // ─────────────────────────────────────────────────────────────────────────
  sections: [
    createSection(
      'overview',
      'Overview',
      `<p>The Analytics Hub is your central dashboard for data-driven insights. It provides reports across multiple dimensions to help you understand performance, utilization, and organizational health.</p>
      <p>Analytics are organized into two main categories:</p>
      <ul>
        <li><strong>Team Analytics</strong> — Reports visible to team leads and above, focused on team-level metrics</li>
        <li><strong>Organization Analytics</strong> — Admin-level reports with cross-team and company-wide visibility</li>
      </ul>
      <p>Additionally, effort-specific reports are available for personal and team time tracking insights.</p>`
    ),
    createSection(
      'team-analytics',
      'Team Analytics',
      `<p>Team-level reports help team leads and managers understand how their team is performing:</p>
      <ul>
        <li><strong>Utilization</strong> — How team capacity is being allocated and used across projects</li>
        <li><strong>Availability</strong> — Current and forecasted team availability for planning</li>
        <li><strong>Project Health</strong> — Status, progress, and risk indicators for team projects</li>
        <li><strong>Effort Variance</strong> — Comparison of planned vs actual effort to spot estimation issues</li>
        <li><strong>Training</strong> — Team learning progress, course completions, and skill development</li>
      </ul>`
    ),
    createSection(
      'org-analytics',
      'Organization Analytics',
      `<p>Admin-level reports provide organization-wide visibility for strategic decision-making:</p>
      <ul>
        <li><strong>Cross-Team Comparison</strong> — Compare metrics across multiple teams side by side</li>
        <li><strong>Cost Analysis</strong> — Budget allocation, spending trends, and cost per project</li>
        <li><strong>Project Portfolio</strong> — Health overview of all active projects across the organization</li>
        <li><strong>Training & Skills</strong> — Organization-wide learning progress and skill gap analysis</li>
        <li><strong>Leave & Attendance</strong> — Patterns in leave usage and attendance trends</li>
        <li><strong>Survey Engagement</strong> — Response rates and sentiment trends from surveys</li>
      </ul>`,
    ),
    createSection(
      'effort-reports',
      'Effort & Time Reports',
      `<p>Effort-specific analytics help track time investment:</p>
      <ul>
        <li><strong>Effort Analytics</strong> — Personal and team effort breakdowns by project, category, and time period</li>
        <li><strong>Team Daily Report</strong> — Day-by-day view of what your team worked on</li>
      </ul>
      <p>These reports rely on timesheet data, so accurate time logging is essential for meaningful insights.</p>`
    ),
    createSection(
      'reading-charts',
      'Reading Charts & Data',
      `<p>Tips for getting the most from your analytics:</p>
      <ul>
        <li><strong>Hover</strong> over chart elements for exact values and tooltips</li>
        <li><strong>Click</strong> on legend items to show/hide specific data series</li>
        <li><strong>Use filters</strong> to narrow data to specific teams, projects, or time periods</li>
        <li><strong>Compare periods</strong> — Select different date ranges to spot trends</li>
        <li><strong>Look for outliers</strong> — Unusual spikes or dips often indicate issues worth investigating</li>
      </ul>`
    ),
  ],

  // ─────────────────────────────────────────────────────────────────────────
  // WALKTHROUGHS
  // ─────────────────────────────────────────────────────────────────────────
  walkthroughs: [
    createWalkthrough(
      'walkthrough-compare-teams',
      'How to Compare Team Performance',
      'Use cross-team analytics to benchmark and compare team metrics.',
      [
        {
          title: 'Navigate to Organization Analytics',
          description: 'Go to Analytics Hub and select "Cross-Team Comparison" from the Organization section.',
        },
        {
          title: 'Select teams to compare',
          description: 'Use the team selector to pick two or more teams you want to compare.',
        },
        {
          title: 'Choose metrics',
          description: 'Select which metrics to compare — utilization, delivery rate, effort variance, etc.',
        },
        {
          title: 'Set the date range',
          description: 'Choose a time period that gives a fair comparison (e.g., same quarter for all teams).',
        },
        {
          title: 'Analyze the results',
          description: 'Review the side-by-side charts. Look for patterns rather than single data points. Export if needed for presentations.',
        },
      ],
      4
    ),
    createWalkthrough(
      'walkthrough-export',
      'How to Export a Report',
      'Export analytics data to CSV or PDF for external use.',
      [
        {
          title: 'Open the desired report',
          description: 'Navigate to the analytics page you want to export.',
        },
        {
          title: 'Apply filters',
          description: 'Set the date range, team, and any other filters to narrow your data first.',
        },
        {
          title: 'Click the Export button',
          description: 'Look for the export icon in the report toolbar (usually top-right). Select CSV or PDF format.',
        },
        {
          title: 'Save the file',
          description: 'The file will download automatically. Open in Excel, Google Sheets, or your preferred tool.',
        },
      ],
      2
    ),
  ],

  // ─────────────────────────────────────────────────────────────────────────
  // TROUBLESHOOTING
  // ─────────────────────────────────────────────────────────────────────────
  troubleshooting: [
    createTroubleshooting(
      'trouble-data-not-loading',
      'Analytics data not loading or showing empty',
      [
        'No timesheet data logged for the selected period',
        'Date range filter set to a period with no activity',
        'Insufficient permissions for the selected report',
        'Network connectivity issues',
      ],
      [
        {
          description: 'Check the date range',
          steps: [
            'Verify the selected date range includes periods with actual data',
            'Try expanding the range to a full month or quarter',
          ],
        },
        {
          description: 'Verify data exists',
          steps: [
            'Confirm that timesheets have been submitted for the selected period',
            'Check that projects have been active during the selected dates',
          ],
        },
        {
          description: 'Check permissions',
          steps: [
            'Team analytics require team lead, manager, or admin role',
            'Organization analytics require admin or manager role',
            'Contact your admin if you need access to specific reports',
          ],
        },
      ]
    ),
    createTroubleshooting(
      'trouble-metrics-mismatch',
      'Metrics seem incorrect or don\'t match expectations',
      [
        'Incomplete timesheet data from team members',
        'Different date ranges being compared',
        'Metrics calculated differently than expected',
      ],
      [
        {
          description: 'Verify data completeness',
          steps: [
            'Check if all team members have submitted timesheets for the period',
            'Missing entries will skew utilization and effort metrics',
          ],
        },
        {
          description: 'Align date ranges',
          steps: [
            'Ensure you\'re comparing the same time periods across different reports',
            'Account for public holidays and leave when interpreting utilization',
          ],
        },
        {
          description: 'Understand the metric definition',
          steps: [
            'Hover over metric labels for tooltips explaining the calculation',
            'Utilization = logged hours / available capacity',
            'Effort variance = (actual - planned) / planned',
          ],
        },
      ]
    ),
  ],

  // ─────────────────────────────────────────────────────────────────────────
  // FAQs
  // ─────────────────────────────────────────────────────────────────────────
  faqs: [
    createFAQ(
      'faq-access',
      'Who can access analytics?',
      'Team members can see their personal metrics on the dashboard. Team leads see team-level analytics. Managers and admins have access to organization-wide reports including cross-team comparisons and cost analysis.',
      ['access', 'permissions', 'roles', 'visibility']
    ),
    createFAQ(
      'faq-refresh',
      'How often is analytics data updated?',
      'Analytics data is refreshed daily based on submitted timesheets and project updates. Some reports may show data up to 24 hours old. For the most current data, ensure your team has submitted their timesheets.',
      ['refresh', 'update', 'frequency', 'real-time', 'delay']
    ),
    createFAQ(
      'faq-export',
      'Can I export analytics reports?',
      'Yes, most reports support CSV export for further analysis in spreadsheets. Look for the export button in the report toolbar. Some reports also support PDF export for sharing in presentations.',
      ['export', 'download', 'CSV', 'PDF', 'spreadsheet']
    ),
    createFAQ(
      'faq-date-range',
      'How do date range filters work?',
      'Date range filters let you focus on a specific time period. Select a preset (This Week, This Month, This Quarter) or choose custom start and end dates. The report will recalculate all metrics for the selected period.',
      ['date', 'range', 'filter', 'period', 'time']
    ),
    createFAQ(
      'faq-utilization',
      'What does utilization percentage mean?',
      'Utilization is calculated as (logged hours / available capacity) x 100%. Available capacity accounts for working hours minus leave and public holidays. A utilization of 80-90% is typically healthy — 100% means no buffer for ad-hoc work.',
      ['utilization', 'percentage', 'capacity', 'calculation']
    ),
    createFAQ(
      'faq-custom-reports',
      'Can I create custom reports?',
      'The current analytics suite provides pre-built reports covering the most common metrics. For custom analysis, export the data to CSV and use your preferred spreadsheet or BI tool. Contact your admin if you need a specific recurring report.',
      ['custom', 'report', 'build', 'create', 'BI']
    ),
    createFAQ(
      'faq-drill-down',
      'How do I drill down into specific data points?',
      'Many charts support drill-down. Click on a bar, slice, or data point to see more detail. For example, clicking a team name in utilization may show individual member breakdowns. Use filters to narrow the focus.',
      ['drill-down', 'detail', 'click', 'expand', 'breakdown']
    ),
    createFAQ(
      'faq-benchmark',
      'Are there benchmarks or targets for metrics?',
      'Some reports show target lines or benchmarks set by your organization. Utilization targets, effort variance thresholds, and training completion goals are configured by admins. Speak to your manager about team-specific targets.',
      ['benchmark', 'target', 'goal', 'threshold', 'KPI']
    ),
    createFAQ(
      'faq-comparison',
      'How do I compare metrics across different time periods?',
      'Most reports allow date range selection. To compare periods, note down or export the current period\'s data, then change the date range to the comparison period. The Cross-Team Comparison report supports side-by-side period comparisons directly.',
      ['compare', 'period', 'trend', 'year-over-year', 'month-over-month']
    ),
  ],

  // ─────────────────────────────────────────────────────────────────────────
  // RELATED LINKS
  // ─────────────────────────────────────────────────────────────────────────
  relatedLinks: [
    createRelatedLink('Timesheet', '/timesheet', { description: 'Log your hours for accurate analytics' }),
    createRelatedLink('Effort Report', '/effort-report', { description: 'View your personal effort metrics' }),
    createRelatedLink('Resources', '/resources', { description: 'Resource allocation and capacity planning' }),
    createRelatedLink('Projects', '/projects', { description: 'View project status and details' }),
  ],

  supportContact: {
    name: 'Analytics Support',
    email: 'support@company.com',
    slackChannel: '#help-analytics',
  },

  keywords: [
    'analytics',
    'reports',
    'metrics',
    'KPIs',
    'utilization',
    'performance',
    'trends',
    'comparison',
    'export',
    'charts',
    'data',
    'insights',
    'dashboard',
    'visualization',
  ],
});

// =============================================================================
// TEAM-LEVEL ANALYTICS SUB-PAGE HELP
// =============================================================================

export const analyticsEffortHelp: HelpContent = createHelpContent({
  id: 'analytics:effort',
  title: 'Effort Analytics',
  description: 'Personal and team effort breakdowns by project and time period',
  parentId: 'analytics',
  sections: [
    createSection('overview', 'Overview', '<p>Effort Analytics shows how time is distributed across projects, tasks, and categories. Compare your logged effort against allocations to identify discrepancies and optimize your time management.</p>'),
    createSection('reading', 'Reading the Report', '<p>The effort breakdown shows:</p><ul><li><strong>By Project</strong> — Hours per project with percentage of total</li><li><strong>By Category</strong> — Development, meetings, admin, etc.</li><li><strong>Trends</strong> — Weekly/monthly effort trends over time</li></ul>'),
  ],
  faqs: [
    createFAQ('faq-1', 'Why does my effort not match my timesheet?', 'Effort analytics aggregates submitted timesheets. Ensure all entries are submitted (not just saved as drafts).', ['effort', 'mismatch', 'timesheet']),
    createFAQ('faq-2', 'Can I see effort for past months?', 'Yes, use the date range filter to view any historical period where timesheet data exists.', ['history', 'past', 'date']),
  ],
  keywords: ['effort', 'time', 'hours', 'breakdown', 'project'],
});

export const analyticsTeamDailyHelp: HelpContent = createHelpContent({
  id: 'analytics:team-daily',
  title: 'Team Daily Report',
  description: 'Day-by-day view of team activities and time logged',
  parentId: 'analytics',
  sections: [
    createSection('overview', 'Overview', '<p>The Team Daily Report shows what each team member worked on each day. Use this to track daily progress, identify blockers, and ensure consistent time logging across the team.</p>'),
    createSection('usage', 'How to Use', '<p>Select a date to see the daily breakdown. Each row shows a team member with their logged hours and project distribution. Look for gaps (members with no entries) to follow up on missing timesheets.</p>'),
  ],
  faqs: [
    createFAQ('faq-1', 'Why are some team members showing zero hours?', 'They may not have logged time for that day yet. Follow up to ensure timesheets are being submitted regularly.', ['zero', 'missing', 'empty']),
    createFAQ('faq-2', 'Can I see daily reports for past dates?', 'Yes, use the date picker to navigate to any past date.', ['history', 'past', 'date']),
  ],
  keywords: ['daily', 'team', 'report', 'day-by-day'],
});

export const analyticsTeamUtilizationHelp: HelpContent = createHelpContent({
  id: 'analytics:team-utilization',
  title: 'Team Utilization',
  description: 'How team capacity is being allocated and used across projects',
  parentId: 'analytics',
  sections: [
    createSection('overview', 'Overview', '<p>Team Utilization measures how effectively your team\'s available hours are being used. It compares logged effort against available capacity (total work hours minus leave and holidays).</p>'),
    createSection('metrics', 'Key Metrics', '<p>Key utilization indicators:</p><ul><li><strong>Overall Utilization %</strong> — Team average across the period</li><li><strong>Individual Utilization</strong> — Per-member breakdown</li><li><strong>Billable vs Non-Billable</strong> — Split between client work and internal activities</li><li><strong>Trend</strong> — Utilization over time to spot patterns</li></ul>'),
    createSection('interpreting', 'Interpreting Results', '<p>Healthy utilization typically ranges from 75-90%. Consistently above 95% may indicate burnout risk. Below 60% might suggest under-allocation or missing timesheet entries.</p>'),
  ],
  faqs: [
    createFAQ('faq-1', 'What is a good utilization rate?', '75-90% is generally healthy. This allows buffer for meetings, admin tasks, and unexpected work. Consistently high utilization (>95%) can lead to burnout.', ['rate', 'target', 'benchmark']),
    createFAQ('faq-2', 'How is capacity calculated?', 'Capacity = working days in period × standard daily hours − leave days − public holidays. Part-time schedules are accounted for if configured.', ['capacity', 'calculation', 'formula']),
    createFAQ('faq-3', 'Why is utilization over 100%?', 'This means team members logged more hours than their available capacity, likely due to overtime or working on holidays/leave days.', ['over', 'exceed', 'overtime']),
  ],
  keywords: ['utilization', 'capacity', 'usage', 'efficiency', 'billable'],
});

export const analyticsTeamAvailabilityHelp: HelpContent = createHelpContent({
  id: 'analytics:team-availability',
  title: 'Team Availability',
  description: 'Current and forecasted team availability for planning',
  parentId: 'analytics',
  sections: [
    createSection('overview', 'Overview', '<p>Team Availability shows who is available, who is on leave, and the team\'s overall capacity for upcoming periods. Use this for sprint planning and resource allocation decisions.</p>'),
    createSection('planning', 'Planning With Availability', '<p>The availability forecast considers:</p><ul><li>Approved leave requests</li><li>Public holidays in each member\'s calendar</li><li>Existing project allocations</li><li>Part-time schedules</li></ul><p>Unallocated capacity represents hours available for new work assignments.</p>'),
  ],
  faqs: [
    createFAQ('faq-1', 'Does availability account for approved leave?', 'Yes, all approved leave requests are automatically deducted from available capacity in the forecast.', ['leave', 'deduct', 'approved']),
    createFAQ('faq-2', 'How far ahead can I see availability?', 'Availability forecasting typically covers 4-8 weeks ahead, depending on how far out leave and allocations have been planned.', ['forecast', 'future', 'weeks']),
  ],
  keywords: ['availability', 'forecast', 'capacity', 'planning', 'leave'],
});

export const analyticsProjectHealthHelp: HelpContent = createHelpContent({
  id: 'analytics:project-health',
  title: 'Project Health',
  description: 'Status, progress, and risk indicators for team projects',
  parentId: 'analytics',
  sections: [
    createSection('overview', 'Overview', '<p>Project Health provides a snapshot of how your team\'s projects are performing. Each project is rated with health indicators based on schedule adherence, effort variance, and task completion rates.</p>'),
    createSection('indicators', 'Health Indicators', '<p>Project health is assessed across:</p><ul><li><strong>On Track</strong> (green) — Project is within schedule and budget</li><li><strong>At Risk</strong> (yellow) — Some metrics are trending outside acceptable ranges</li><li><strong>Off Track</strong> (red) — Significant schedule or budget overruns</li></ul>'),
  ],
  faqs: [
    createFAQ('faq-1', 'How is project health determined?', 'Health is calculated from task completion rate, effort variance (planned vs actual), and milestone adherence. Projects significantly behind on any metric are flagged.', ['calculation', 'formula', 'how']),
    createFAQ('faq-2', 'Can I override the health status?', 'Project leads can manually set a project\'s health status if the automated assessment doesn\'t reflect reality (e.g., deliberate scope changes).', ['override', 'manual', 'change']),
  ],
  keywords: ['project', 'health', 'status', 'risk', 'on-track'],
});

export const analyticsEffortVarianceHelp: HelpContent = createHelpContent({
  id: 'analytics:effort-variance',
  title: 'Effort Variance',
  description: 'Comparison of planned vs actual effort to improve estimation',
  parentId: 'analytics',
  sections: [
    createSection('overview', 'Overview', '<p>Effort Variance compares the estimated effort for tasks and projects against the actual time logged. This helps teams improve their estimation accuracy over time.</p>'),
    createSection('reading', 'Reading the Variance', '<p>Variance is expressed as a percentage:</p><ul><li><strong>0%</strong> — Perfect estimation, actual matched planned</li><li><strong>Positive (e.g., +30%)</strong> — Over-spent: took more time than planned</li><li><strong>Negative (e.g., -20%)</strong> — Under-spent: finished quicker than planned</li></ul><p>Consistent positive variance suggests tasks are being underestimated. Consistent negative variance may mean over-estimation or incomplete time logging.</p>'),
  ],
  faqs: [
    createFAQ('faq-1', 'What is an acceptable effort variance?', 'Generally ±15% is considered good estimation. Beyond ±30% warrants review of estimation practices.', ['acceptable', 'threshold', 'good']),
    createFAQ('faq-2', 'Does variance account for scope changes?', 'Not automatically. If scope changed mid-project, the original estimate may no longer be fair to compare against. Consider updating estimates when scope changes significantly.', ['scope', 'change', 'estimate']),
  ],
  keywords: ['variance', 'planned', 'actual', 'estimation', 'accuracy'],
});

export const analyticsTeamTrainingHelp: HelpContent = createHelpContent({
  id: 'analytics:team-training',
  title: 'Team Training Analytics',
  description: 'Team learning progress, course completions, and skill development',
  parentId: 'analytics',
  sections: [
    createSection('overview', 'Overview', '<p>Team Training Analytics shows your team\'s progress across learning initiatives. Track course enrollments, completions, mandatory training compliance, and skill coverage.</p>'),
    createSection('metrics', 'Key Metrics', '<p>Available metrics include:</p><ul><li><strong>Completion Rate</strong> — Percentage of enrolled courses completed</li><li><strong>Mandatory Compliance</strong> — Status of required training across the team</li><li><strong>Learning Hours</strong> — Total time invested in training</li><li><strong>Skill Coverage</strong> — Which skills are covered and where gaps exist</li></ul>'),
  ],
  faqs: [
    createFAQ('faq-1', 'Can I see individual team member training?', 'Yes, drill down on team members to see their individual course progress, completions, and upcoming deadlines.', ['individual', 'member', 'detail']),
    createFAQ('faq-2', 'How do I ensure mandatory training is completed?', 'The mandatory compliance metric flags team members who are behind. You can send reminders directly from the report.', ['mandatory', 'compliance', 'reminder']),
  ],
  keywords: ['training', 'learning', 'courses', 'skills', 'compliance'],
});

// =============================================================================
// ORGANIZATION-LEVEL ANALYTICS SUB-PAGE HELP
// =============================================================================

export const analyticsOrgUtilizationHelp: HelpContent = createHelpContent({
  id: 'analytics:org-utilization',
  title: 'Organization Utilization',
  description: 'Company-wide utilization metrics and capacity overview',
  parentId: 'analytics',
  sections: [
    createSection('overview', 'Overview', '<p>Organization Utilization provides a bird\'s-eye view of how the entire company\'s capacity is being used. Compare departments, identify under-utilized teams, and spot capacity bottlenecks.</p>'),
    createSection('departments', 'Department View', '<p>See utilization broken down by department or business unit. This helps leadership make staffing and allocation decisions based on where capacity exists.</p>'),
  ],
  faqs: [
    createFAQ('faq-1', 'How is org-wide utilization different from team utilization?', 'Org utilization aggregates all teams and departments, giving a macro view. Team utilization focuses on a single team\'s metrics.', ['org', 'company', 'aggregate']),
    createFAQ('faq-2', 'Can I see historical trends?', 'Yes, use the date range to compare utilization across months or quarters. Look for seasonal patterns.', ['history', 'trend', 'seasonal']),
  ],
  keywords: ['organization', 'utilization', 'company-wide', 'departments'],
  visibility: { visibleTo: ['admin', 'manager'] },
});

export const analyticsCrossTeamHelp: HelpContent = createHelpContent({
  id: 'analytics:cross-team',
  title: 'Cross-Team Comparison',
  description: 'Compare metrics across multiple teams side by side',
  parentId: 'analytics',
  sections: [
    createSection('overview', 'Overview', '<p>Cross-Team Comparison allows you to benchmark teams against each other across key metrics. Use this to identify best practices, spot struggling teams early, and make informed resource allocation decisions.</p>'),
    createSection('usage', 'How to Compare', '<p>Select two or more teams, choose the metrics to compare (utilization, delivery, effort variance, training), and set a common date range. Results display side-by-side for easy comparison.</p>'),
  ],
  faqs: [
    createFAQ('faq-1', 'Is cross-team comparison fair for different-sized teams?', 'Metrics are normalized to percentages where possible (e.g., utilization %). Absolute numbers like total hours will naturally differ for different team sizes.', ['fair', 'normalize', 'size']),
    createFAQ('faq-2', 'Can I share comparisons with stakeholders?', 'Yes, export the comparison to CSV or take a screenshot for presentations.', ['share', 'export', 'presentation']),
  ],
  keywords: ['cross-team', 'comparison', 'benchmark', 'side-by-side'],
  visibility: { visibleTo: ['admin', 'manager'] },
});

export const analyticsCostAnalysisHelp: HelpContent = createHelpContent({
  id: 'analytics:cost-analysis',
  title: 'Cost Analysis',
  description: 'Budget allocation, spending trends, and cost per project',
  parentId: 'analytics',
  sections: [
    createSection('overview', 'Overview', '<p>Cost Analysis tracks how budgets are being spent across projects and teams. It uses effort data combined with team member cost rates to calculate actual cost vs planned budget.</p>'),
    createSection('metrics', 'Key Metrics', '<p>Available cost metrics include:</p><ul><li><strong>Cost per Project</strong> — Total spend based on logged hours × member rates</li><li><strong>Budget Burn Rate</strong> — How quickly budget is being consumed</li><li><strong>Cost vs Budget</strong> — Actual spending compared to approved budget</li><li><strong>Forecasted Spend</strong> — Projected total cost based on current burn rate</li></ul>'),
  ],
  faqs: [
    createFAQ('faq-1', 'How are costs calculated?', 'Costs are based on logged hours multiplied by each team member\'s monthly cost rate (set in admin settings). This includes both internal team members and external contractors if configured.', ['calculation', 'cost', 'rate']),
    createFAQ('faq-2', 'What if cost data looks wrong?', 'Check that team members have their monthly cost configured in admin settings. Missing cost rates will show as zero, skewing averages.', ['wrong', 'incorrect', 'missing']),
  ],
  keywords: ['cost', 'budget', 'spending', 'financial', 'rate'],
  visibility: { visibleTo: ['admin', 'manager'] },
});

export const analyticsProjectPortfolioHelp: HelpContent = createHelpContent({
  id: 'analytics:project-portfolio',
  title: 'Project Portfolio',
  description: 'Health overview of all active projects across the organization',
  parentId: 'analytics',
  sections: [
    createSection('overview', 'Overview', '<p>The Project Portfolio gives leadership a consolidated view of all active projects. See which projects are on track, at risk, or off track at a glance, along with resource allocation and timeline status.</p>'),
    createSection('views', 'Portfolio Views', '<p>The portfolio can be viewed as:</p><ul><li><strong>Status Grid</strong> — Color-coded health status for each project</li><li><strong>Timeline View</strong> — Gantt-style view of project schedules</li><li><strong>Resource View</strong> — Allocation distribution across projects</li></ul>'),
  ],
  faqs: [
    createFAQ('faq-1', 'What projects appear in the portfolio?', 'All active projects are included. Completed and archived projects are excluded by default but can be shown with a filter toggle.', ['active', 'include', 'filter']),
    createFAQ('faq-2', 'Can I prioritize projects in the portfolio view?', 'Projects can be sorted by health status, budget, team size, or custom priority. Use the sort controls in the toolbar.', ['sort', 'priority', 'order']),
  ],
  keywords: ['portfolio', 'projects', 'overview', 'status', 'health'],
  visibility: { visibleTo: ['admin', 'manager'] },
});

export const analyticsOrgTrainingSkillsHelp: HelpContent = createHelpContent({
  id: 'analytics:org-training-skills',
  title: 'Organization Training & Skills',
  description: 'Company-wide learning progress and skill gap analysis',
  parentId: 'analytics',
  sections: [
    createSection('overview', 'Overview', '<p>Organization Training & Skills provides a macro view of learning across the company. Track overall course completion rates, identify skill gaps, and measure the impact of training investments.</p>'),
    createSection('skills', 'Skill Gap Analysis', '<p>The skill gap view shows:</p><ul><li>Skills currently covered by team capabilities</li><li>Skills required but lacking across the organization</li><li>Training programs that address identified gaps</li><li>Department-level skill distribution</li></ul>'),
  ],
  faqs: [
    createFAQ('faq-1', 'How are skill gaps identified?', 'Skill gaps are determined by comparing role requirements with completed training and self-assessed competencies. Gaps highlight areas where training investment would have the most impact.', ['gap', 'identify', 'requirement']),
    createFAQ('faq-2', 'Can I see training ROI?', 'The report includes learning hours invested and completion rates. Pair this with performance metrics for a more complete ROI picture.', ['ROI', 'return', 'investment']),
  ],
  keywords: ['training', 'skills', 'gap', 'organization', 'learning'],
  visibility: { visibleTo: ['admin', 'manager'] },
});

export const analyticsLeaveAttendanceHelp: HelpContent = createHelpContent({
  id: 'analytics:leave-attendance',
  title: 'Leave & Attendance',
  description: 'Patterns in leave usage and attendance trends across the organization',
  parentId: 'analytics',
  sections: [
    createSection('overview', 'Overview', '<p>Leave & Attendance analytics reveal patterns in time-off usage across the organization. Identify seasonal trends, check if leave policies are being used equitably, and plan for periods of low availability.</p>'),
    createSection('metrics', 'Key Metrics', '<p>Available metrics include:</p><ul><li><strong>Leave Usage by Type</strong> — Breakdown of vacation, sick, personal leave</li><li><strong>Monthly Trends</strong> — Which months have the most leave</li><li><strong>Department Comparison</strong> — Leave patterns across teams</li><li><strong>Unutilized Leave</strong> — Employees with significant unused leave balances</li></ul>'),
  ],
  faqs: [
    createFAQ('faq-1', 'Does this include pending leave requests?', 'By default, only approved leave is included. Toggle the "Include Pending" filter to see requested but not yet approved leave.', ['pending', 'approved', 'filter']),
    createFAQ('faq-2', 'Can I see individual leave details?', 'For privacy, individual leave details are only visible to direct managers and HR admins. Aggregate team data is shown at the organization level.', ['individual', 'privacy', 'detail']),
  ],
  keywords: ['leave', 'attendance', 'absence', 'vacation', 'sick'],
  visibility: { visibleTo: ['admin', 'manager'] },
});

export const analyticsSurveyEngagementHelp: HelpContent = createHelpContent({
  id: 'analytics:survey-engagement',
  title: 'Survey Engagement',
  description: 'Response rates and sentiment trends from organizational surveys',
  parentId: 'analytics',
  sections: [
    createSection('overview', 'Overview', '<p>Survey Engagement analytics track how well employees engage with surveys. Monitor response rates, identify teams with low participation, and analyze sentiment trends across survey rounds.</p>'),
    createSection('metrics', 'Key Metrics', '<p>Available metrics include:</p><ul><li><strong>Response Rate</strong> — Percentage of targeted employees who completed the survey</li><li><strong>Completion Time</strong> — Average time to complete surveys</li><li><strong>Sentiment Trend</strong> — NPS or satisfaction scores over time</li><li><strong>Department Breakdown</strong> — Engagement by team or department</li></ul>'),
  ],
  faqs: [
    createFAQ('faq-1', 'Are survey responses anonymous in analytics?', 'Yes, individual responses are never identifiable in analytics. All data is aggregated at the team or department level with a minimum threshold to prevent identification.', ['anonymous', 'privacy', 'identifiable']),
    createFAQ('faq-2', 'How do I improve survey response rates?', 'Keep surveys short, communicate their purpose clearly, and share results and actions taken from previous surveys. Teams that see results act on surveys tend to participate more.', ['improve', 'response', 'rate']),
  ],
  keywords: ['survey', 'engagement', 'response', 'NPS', 'sentiment'],
  visibility: { visibleTo: ['admin', 'manager'] },
});
